import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/login.css';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await fetch('/api/v1/owner/auth/request-password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message || 'If an account exists, a reset link has been sent');
        setEmail('');
      } else {
        setError(data.error || 'Failed to request password reset');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Forgot password?</h1>
          <p style={styles.subtitle}>
            Enter your email address and we'll send you a password reset link.
          </p>
        </div>

        {message && (
          <div 
            id="forgot-success"
            role="alert"
            style={styles.success}
            aria-live="polite"
          >
            {message}
          </div>
        )}

        {error && (
          <div 
            id="forgot-error"
            role="alert"
            style={styles.error}
            aria-live="polite"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label htmlFor="email" style={styles.label}>
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoComplete="email"
              style={{
                ...styles.input,
                ...(loading ? styles.inputDisabled : {}),
              }}
              aria-describedby={error ? 'forgot-error' : message ? 'forgot-success' : undefined}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !email.trim()}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            aria-busy={loading}
          >
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>

        <div style={styles.footer}>
          <button
            type="button"
            onClick={() => navigate('/login')}
            style={styles.linkButton}
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    margin: '0 auto',
    padding: 'var(--spacing-4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    padding: 'var(--spacing-8)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--elevation-3)',
    border: '1px solid var(--color-border-light, #e8e8ea)',
    width: '100%',
    maxWidth: '440px',
  },
  header: {
    textAlign: 'center',
    marginBottom: 'var(--spacing-6)',
  },
  title: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    lineHeight: 'var(--line-height-tight-medium)',
    letterSpacing: 'var(--letter-spacing-tight-medium)',
    margin: 0,
    marginBottom: 'var(--spacing-2)',
  },
  subtitle: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
    lineHeight: 'var(--line-height-relaxed)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-4)',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
  },
  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-1)',
  },
  input: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    border: '1px solid var(--color-border-light, #e8e8ea)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-base)',
    fontFamily: 'var(--font-family-primary)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-primary)',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base), background-color var(--transition-base)',
    outline: 'none',
    width: '100%',
  },
  inputDisabled: {
    backgroundColor: 'var(--color-bg-tertiary, #eceef0)',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  success: {
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-semantic-success-bg, rgba(16, 185, 129, 0.1))',
    color: 'var(--color-semantic-success-text, #065f46)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-semantic-success-base, #10b981)',
    lineHeight: 'var(--line-height-normal)',
    marginBottom: 'var(--spacing-4)',
  },
  error: {
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-semantic-error-bg, rgba(239, 68, 68, 0.1))',
    color: 'var(--color-semantic-error-text, #991b1b)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    border: '1px solid var(--color-semantic-error-base, #ef4444)',
    lineHeight: 'var(--line-height-normal)',
    marginBottom: 'var(--spacing-4)',
  },
  button: {
    width: '100%',
    padding: 'var(--spacing-3) var(--spacing-4)',
    backgroundColor: 'var(--color-brand-primary, #0F4C75)',
    color: 'var(--color-text-inverse, #ffffff)',
    border: '1px solid var(--color-brand-primary, #0F4C75)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    cursor: 'pointer',
    transition: 'background-color var(--transition-base), border-color var(--transition-base), transform var(--transition-fast)',
    outline: 'none',
    marginTop: 'var(--spacing-2)',
  },
  buttonDisabled: {
    backgroundColor: 'var(--color-text-disabled, #a3a3a3)',
    borderColor: 'var(--color-text-disabled, #a3a3a3)',
    cursor: 'not-allowed',
    transform: 'none',
  },
  footer: {
    marginTop: 'var(--spacing-6)',
    textAlign: 'center',
    paddingTop: 'var(--spacing-4)',
    borderTop: '1px solid var(--color-border-light, #e8e8ea)',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: 'var(--color-brand-primary, #0F4C75)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    textDecoration: 'none',
    padding: 0,
    transition: 'color var(--transition-base)',
  },
};
